/*
Badges screen — grid of all 15 badges, grouped by category. Locked
badges are shown desaturated with the criterion under the title; unlocked
badges show the date of the capture that unlocked them.

Mirrors Android BadgesScreen.kt. The 4 unimplementable badges (TOURNEUR,
TOUR_COMPLET, GEANT, VIEUX_SAGE) are kept in the catalogue for visual
parity with the Android app and are tagged with the "Donnée non
disponible" hint instead of a criterion.
*/

import { navigate, kPaths } from '../router'
import { listCaptures } from '../data/captureRepository'
import { ensureArbresFeatures } from '../data/arbresFeatures'
import { ensureArbresDetails } from '../data/arbresDetails'
import { ensureSpeciesInfo } from '../data/speciesInfo'
import { evaluateBadges } from '../data/badgeEvaluator'
import {
	kBadgeCatalog,
	kBadgeCategoryLabels,
	kBadgeGlyphs,
	type BadgeCategory,
	type BadgeState,
} from '../data/badge'
import { renderSkeletonBlock, renderSkeletonBadgeTile } from './components/Skeleton'

const kFrenchDate = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })

/*
Mount the badges screen. Renders a static shell synchronously, then
hydrates the unlock state async.
*/
export function mountBadgesScreen(inRoot: HTMLElement): void
{
	// Skeleton tiles roughly match the badge-grid count so the loading
	// view fills the same vertical space the populated grid will.
	const vSkeletonTiles = Array.from({ length: kBadgeCatalog.length }, () => renderSkeletonBadgeTile()).join('')
	inRoot.innerHTML = `
		<div class="screen badges">
			<header class="arboretum-header">
				<button class="nav-back" id="nav-back" aria-label="Retour">←</button>
				<h1 class="display arboretum-title">Badges</h1>
			</header>
			<div class="arboretum-subheader" id="badges-subheader">
				${renderSkeletonBlock({ width: '160px', height: '14px' })}
			</div>
			<div class="badges-grid-wrap" id="badges-wrap">
				<div class="badges-grid skeleton-badge-grid">${vSkeletonTiles}</div>
			</div>
		</div>
	`
	inRoot.querySelector('#nav-back')?.addEventListener('click', () => {
		if (history.length > 1) history.back()
		else navigate(kPaths.profile())
	})
	void hydrate(inRoot)
}

async function hydrate(inRoot: HTMLElement): Promise<void>
{
	const vSubheader = inRoot.querySelector<HTMLElement>('#badges-subheader')
	const vWrap = inRoot.querySelector<HTMLElement>('#badges-wrap')
	if (!vSubheader || !vWrap) return

	try
	{
		const [vCaptures, vFeatures, vInfo, vDetails] = await Promise.all([
			listCaptures(),
			ensureArbresFeatures(),
			ensureSpeciesInfo(),
			ensureArbresDetails(),
		])
		const vById = new Map<number, typeof vFeatures[number]>()  // arbreId -> feature, only for ones we capture
		for (const vCap of vCaptures)
		{
			const vF = vFeatures.find((inF) => inF.id === vCap.arbreId)
			if (vF) vById.set(vCap.arbreId, vF)
		}

		const vStates = evaluateBadges({ captures: vCaptures, arbresById: vById, detailsById: vDetails, speciesInfo: vInfo })
		const vUnlocked = vStates.filter((inS) => inS.unlockedAt !== null).length

		vSubheader.innerHTML = `
			<span class="arboretum-counter">
				<strong>${vUnlocked}</strong> / ${kBadgeCatalog.length} débloquées
			</span>
		`

		vWrap.innerHTML = renderGroups(vStates)
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
		vSubheader.innerHTML = `<span class="arboretum-error">⚠ ${escapeHtml(vMsg)}</span>`
		vWrap.innerHTML = ''
	}
}

/*
Render the grid grouped by category, in catalogue order. Each section
header is sticky-ish (just visually separated, not position:sticky) so
the grid stays simple.
*/
function renderGroups(inStates: BadgeState[]): string
{
	const vByCategory = new Map<BadgeCategory, BadgeState[]>()  // category -> badges, preserves catalogue order
	for (const vS of inStates)
	{
		const vBucket = vByCategory.get(vS.def.category) ?? []
		vBucket.push(vS)
		vByCategory.set(vS.def.category, vBucket)
	}

	const vCategories: BadgeCategory[] = ['decouverte', 'botanique', 'geographie', 'remarquables', 'saisons', 'demesure']

	return vCategories.map((inCat) => {
		const vBucket = vByCategory.get(inCat) ?? []
		if (vBucket.length === 0) return ''
		const vCards = vBucket.map(renderBadgeCard).join('')
		return `
			<section class="badges-section">
				<h2 class="badges-section-title">${escapeHtml(kBadgeCategoryLabels[inCat])}</h2>
				<div class="badges-grid">${vCards}</div>
			</section>
		`
	}).join('')
}

function renderBadgeCard(inState: BadgeState): string
{
	const vUnlocked = inState.unlockedAt !== null
	const vGlyph = kBadgeGlyphs[inState.def.category]
	const vUnlockedDate = vUnlocked ? kFrenchDate.format(new Date(inState.unlockedAt as number)) : ''
	const vBlocked = !inState.def.implemented
	const vSubtitle = vBlocked
		? 'Donnée non disponible'
		: inState.def.description

	return `
		<div class="badge-card" data-unlocked="${vUnlocked ? 'true' : 'false'}" data-blocked="${vBlocked ? 'true' : 'false'}">
			<div class="badge-glyph" aria-hidden="true">${vGlyph}</div>
			<div class="badge-label">${escapeHtml(inState.def.label)}</div>
			<div class="badge-criterion">${escapeHtml(vSubtitle)}</div>
			${vUnlocked ? `<div class="badge-unlocked-at">Débloqué le ${vUnlockedDate}</div>` : '<div class="badge-locked-pill">🔒</div>'}
		</div>
	`
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}
